import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuthToken } from '#/api/auth';
import { userUnauthorizedTopic } from '#/features/auth/auth-topics';

export interface BaseReq {
  readonly signal?: AbortSignal;
  readonly body?: unknown;
  readonly headers?: Record<string, string>;
}

export interface BasePaginatedReq extends BaseReq {
  readonly cursor?: string | null;
  readonly page_size?: number;
}

interface BaseResp<T> {
  readonly ok: boolean;
  readonly status: number;
  readonly data: T;
}

export interface SuccessResp<T> extends BaseResp<T> {
  readonly ok: true;
}

// WIP default type for failed response data T to be defined
export interface FailedResp<T = unknown> extends BaseResp<T> {
  readonly error: Error;
  /** User friendly message to be displayed when needed */
  readonly message: string;
  readonly ok: false;
}

export type Resp<S, F = unknown> = SuccessResp<S> | FailedResp<F>;

export type AsyncResp<S, F = unknown> = Promise<Resp<S, F>>;

export interface PaginatedRespData<T> {
  readonly prev: string | null;
  readonly next: string | null;
  readonly results: readonly T[];
}

export type ApiFn<R extends BaseReq, T> = (req: R) => AsyncResp<T>;

export interface ApiState<T> {
  readonly isLoading: boolean;
  readonly resp: Resp<T> | null;
}

export interface ApiCall<T = unknown> {
  /**
   * Avoid using this as you will have to handle the _AbortError_.
   * Prefer using the callback on the execute function.
   */
  readonly promise: AsyncResp<T>;
  readonly abort: () => void;
}

const INITIAL_STATE = {
  isLoading: false,
  resp: null,
};

export function useApi<R extends BaseReq, T>(
  api: ApiFn<R, T>,
  initialState: ApiState<T> = INITIAL_STATE,
): [
  state: ApiState<T>,
  execute: (req: R, cb?: (resp: Resp<T>) => void) => ApiCall<T>,
] {
  const abortControllerSetRef = useRef<Set<AbortController>>();

  useEffect(
    () => () => {
      const abortControllerSet = abortControllerSetRef.current;
      if (abortControllerSet == null) return;
      for (const abortController of abortControllerSet) {
        abortController.abort();
      }
    },
    [],
  );

  const [state, setState] = useState(initialState);

  const exec = useCallback(
    (req: R, cb?: (resp: Resp<T>) => void): ApiCall<T> => {
      setState((currState) => ({ ...currState, isLoading: true }));

      let finalReq: R;
      let abortController: AbortController | undefined;

      // only create a controller when caller is not using its own signal
      if (req.signal == null) {
        abortController = new AbortController();
        abortControllerSetRef.current ??= new Set();
        abortControllerSetRef.current.add(abortController);
        finalReq = { ...req, signal: abortController.signal };
      } else {
        finalReq = req;
      }

      const callApi = async () => {
        try {
          const resp = await api(finalReq);
          // skip side effects in case the request was aborted
          if (resp.ok || !isAbortError(resp.error)) {
            cb?.(resp);
            setState({ isLoading: false, resp });
          }
          return resp;
        } finally {
          // remove abort controller from set so we don't leak memory
          if (abortController != null)
            abortControllerSetRef.current?.delete(abortController);
        }
      };

      return {
        promise: callApi(),
        abort: () => {
          if (abortController == null) {
            throw new Error(
              "You've provided your own signal, use your abort controller to cancel.",
            );
          }
          abortController.abort();
        },
      };
    },
    [api],
  );

  return [state, exec];
}

export interface RequestParams extends BaseReq {
  readonly url: string;
  readonly method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'HEAD';
}

export async function requestApi<T>(params: RequestParams): AsyncResp<T> {
  const resp = await requestInternal<T>(API_URL, params);

  authListener(resp);

  return resp;
}

function authListener<T>(response: Resp<T>) {
  const isUnauthorized =
    // `401` when common API request like `GET /positions` fails due to expired JWT
    response.status === 401;

  if (isUnauthorized) {
    userUnauthorizedTopic.publish({});
  }
}

async function parseJson(resp: Response): Promise<unknown> {
  const contentType = resp.headers.get('content-type');
  if (contentType == null) return null;
  return contentType.includes('application/json') ? resp.json() : null;
}

async function requestInternal<T>(
  baseUrl: string,
  params: RequestParams,
): AsyncResp<T> {
  const { url, method, headers, body, signal } = params;

  const baseHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAuthToken()}`,
  };

  let resp;
  try {
    resp = await fetch(`${baseUrl}${url}`, {
      method,
      headers: {
        ...baseHeaders,
        ...headers,
      },
      signal,
      body: JSON.stringify(body),
      credentials: 'omit',
    });
  } catch (_error) {
    const cause = _error as Error;
    const error = new Error('Failed to connect to Paradex servers', { cause });

    return {
      ok: false,
      status: 0,
      error,
      message: error.message,
      data: {},
    };
  }

  let data: unknown;
  try {
    data = await parseJson(resp);
  } catch (_error) {
    const cause = _error as Error;

    if (cause.name === 'SyntaxError') {
      // eslint-disable-next-line no-console
      console.error({
        message: 'Failed parsing API response as JSON',
        data: { method, url, status: resp.status },
        error: cause,
      });
    }

    const error = new Error('Failed parsing API response', { cause });

    return {
      ok: false,
      status: resp.status,
      error,
      message: error.message,
      data: {},
    };
  }

  if (!resp.ok) {
    const msg = data != null ? JSON.stringify(data) : null;
    const error = new Error(`Request failed with status ${resp.status}`, {
      cause: msg,
    });

    return {
      ok: false,
      status: resp.status,
      error,
      message: msg ?? error.message,
      data,
    };
  }

  return {
    ok: true,
    status: resp.status,
    data: data as T,
  };
}

export function isAbortError(error: Error): boolean {
  if (error.name === 'AbortError') {
    return true;
  }
  if (error.cause instanceof Error) {
    return isAbortError(error.cause);
  }
  return false;
}
